import { Box, ConditionWrapper, Flex, Grid } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue } from 'jotai';
import { backend } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { getContext } from 'pages/requestForm/selectors/pageSelectors';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useLifecycles } from 'react-use';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteVendorFieldSettings, useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { NetSuiteVendorForm } from '../components/NetSuiteVendorForm/NetSuiteVendorForm';
import {
    getVendorTransfer,
    getVendorValidationErrors,
} from '../components/NetSuiteVendorForm/NetSuiteVendorForm.helpers';
import { convertCustomFieldsToState } from '../helpers';
import { useResetVendorFormState, useUploadAttachments } from '../hooks';
import { isFieldMandatory, isFieldVisible } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteCustomFieldsState } from '../resources/states';
import { messages } from './NetSuiteVendorRequestForm.messages';
import { netSuiteVendorFieldValuesState } from './NetSuiteVendorRequestForm.states';
import { Content, Root, ToolbarContainerBox, ToolbarTextFlex } from './NetSuiteVendorRequestForm.styles';
import { NetSuiteVendorRequestFormProps } from './NetSuiteVendorRequestForm.types';

export const NetSuiteVendorRequestForm: FC<NetSuiteVendorRequestFormProps> = memo((props) => {
    const { request } = props;

    const pageContext = useSelector(getContext);

    const { details } = request;

    const { terms, currencies, currentSubsidiary, documentFields } = useSelector(getNetSuiteContext);

    const fieldSettings = useNetSuiteVendorFieldSettings(documentFields);

    const fieldValues = useAtomValue(netSuiteVendorFieldValuesState);
    const [customFieldsValues, setCustomFieldsValues] = useAtom(netSuiteCustomFieldsState);
    const resetVendorFormState = useResetVendorFormState();
    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);

    const [showErrors, setShowErrors] = useState(false);

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const requestSubsidiary = useMemo(() => {
        if (details.primarySubsidiary) {
            return {
                id: details.primarySubsidiary.id,
                text: details.primarySubsidiary.name,
            };
        }

        if (currentSubsidiary) {
            return {
                id: currentSubsidiary.id,
                text: currentSubsidiary.text,
            };
        }

        return null;
    }, [details.primarySubsidiary, currentSubsidiary]);

    useLifecycles(
        () => {
            const { customFields } = details;

            setCustomFieldsValues(
                customFields && customFields.length > 0 ? convertCustomFieldsToState(customFields) : {}
            );
        },
        () => {
            resetVendorFormState();
        }
    );

    const getRequestTransfer = useCallback((): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            amount: null,
            currency: null,
            exchangeRate: null,
            exchangeRateSource: null,
            exchangeRateDate: null,
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        if (fieldValues) {
            transfer.netSuiteVendorDetails = getVendorTransfer(fieldValues, customFieldsValues);
        }

        return transfer;
    }, [
        fieldValues,
        customFieldsValues,
        pageContext.templateVersion,
        request.attachments,
        request.companyId,
        request.id,
        request.requestNote,
    ]);

    const getValidationErrors = useCallback(async () => {
        setShowErrors(true);

        return getVendorValidationErrors({
            fieldValues,
            fieldSettings,
            attachmentsFieldSettings,
            attachments: request.attachments,
        });
    }, [fieldValues, fieldSettings, attachmentsFieldSettings, request.attachments]);

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <ToolbarContainerBox spacing='25 80 30'>
                    <Flex alignItems='center' spacing='0'>
                        <ToolbarTextFlex grow={1}>{messages.toolbarText}</ToolbarTextFlex>

                        <ActionsBlock
                            customGetRequestTransfer={getRequestTransfer}
                            customRequestValidationFn={getValidationErrors}
                        />
                    </Flex>
                </ToolbarContainerBox>

                <Content>
                    <Box spacing='0 0 40'>
                        <Flex direction='column' spacing='0' wrap={false}>
                            <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                            <SectionContainer>
                                <NetSuiteVendorForm
                                    companyId={request.companyId}
                                    workflowId={request.template.id}
                                    terms={terms || []}
                                    currencies={currencies || []}
                                    requestSubsidiary={requestSubsidiary}
                                    showErrors={showErrors}
                                    details={details}
                                    isPopup={false}
                                />
                            </SectionContainer>

                            {isFieldVisible(attachmentsFieldSettings.attachments) &&
                                (isBelgradeUIFilesUploadSection ? (
                                    <Grid gridTemplateColumns='50%' gap={16}>
                                        <AttachmentsSection
                                            request={request}
                                            required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                        />
                                    </Grid>
                                ) : (
                                    <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                                ))}
                        </Flex>
                    </Box>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteVendorRequestForm.displayName = 'NetSuiteVendorRequestForm';
